import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Mail, KeyRound, Eye, EyeOff, Shield } from 'lucide-react';
import { useAdmin } from '../context/AdminContext';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/translations';

const AdminLogin = () => {
  const navigate = useNavigate();
  const { login } = useAdmin();
  const { language } = useLanguage();
  const t = translations[language] || translations.pt;

  // Set page title and favicon for admin
  useEffect(() => {
    document.title = language === 'pt' ? 'Sancur Admin | Painel de Controlo' : 'Sancur Admin | Control Panel';
  }, [language]);

  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockTimer, setLockTimer] = useState(0);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if locked
    if (isLocked) {
      setError(language === 'pt' 
        ? `Aguarde ${lockTimer} segundos antes de tentar novamente` 
        : `Wait ${lockTimer} seconds before trying again`);
      return;
    }
    
    setIsSubmitting(true);
    setError('');

    const result = await login(formData.email, formData.password);
    
    if (result.success) {
      setAttempts(0);
      navigate('/admin/dashboard');
    } else {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      
      // Lock after 3 failed attempts
      if (newAttempts >= 3) {
        setIsLocked(true);
        let seconds = 30;
        setLockTimer(seconds);
        
        const interval = setInterval(() => {
          seconds--;
          setLockTimer(seconds);
          if (seconds <= 0) {
            clearInterval(interval);
            setIsLocked(false);
            setAttempts(0);
          }
        }, 1000);
        
        setError(language === 'pt' 
          ? 'Demasiadas tentativas. Conta bloqueada por 30 segundos.' 
          : 'Too many attempts. Account locked for 30 seconds.');
      } else {
        setError(language === 'pt' 
          ? `Credenciais inválidas. Tentativas restantes: ${3 - newAttempts}` 
          : `Invalid credentials. Remaining attempts: ${3 - newAttempts}`);
      }
    }
    
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" data-testid="admin-login-page">
      <div className="max-w-md w-full">
        <div className="bg-white p-8 rounded-xl shadow-lg">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_trusted-maintenance-1/artifacts/l9fyih4f_Whisk_4dd066d44f7c3e88b4a47b0cc96820dddr-removebg-preview.png" 
                alt="Sancur Services" 
                className="h-24 w-auto"
              />
            </div>
            <h1 className="text-3xl font-heading font-bold text-gray-900 mb-2">
              {t.admin.login.title}
            </h1>
            <p className="text-gray-600 font-body">{t.admin.login.subtitle}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">
            <div>
              <label htmlFor="admin-email" className="block text-sm font-body font-medium text-gray-700 mb-2">
                {t.admin.login.email}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail size={20} className="text-gray-400" />
                </div>
                <input
                  type="email"
                  id="admin-email"
                  name="email"
                  data-testid="admin-input-email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  autoComplete="new-email"
                  disabled={isLocked}
                  className="w-full h-12 pl-10 pr-4 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent font-body disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder={language === 'pt' ? 'Introduza o seu email' : 'Enter your email'}
                />
              </div>
            </div>

            <div>
              <label htmlFor="admin-password" className="block text-sm font-body font-medium text-gray-700 mb-2">
                {t.admin.login.password}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <KeyRound size={20} className="text-gray-400" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  id="admin-password"
                  name="password"
                  data-testid="admin-input-password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  autoComplete="new-password"
                  disabled={isLocked}
                  className="w-full h-12 pl-10 pr-12 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent font-body disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder={language === 'pt' ? 'Introduza a sua senha' : 'Enter your password'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff size={20} className="text-gray-400 hover:text-gray-600" />
                  ) : (
                    <Eye size={20} className="text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div className="p-4 bg-red-50 text-red-800 rounded-md border border-red-200 text-sm" data-testid="admin-login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={isSubmitting || isLocked}
              className="w-full h-12 bg-primary text-white font-body font-semibold rounded-md hover:bg-primary-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLocked ? (
                <>
                  <Lock size={18} />
                  {language === 'pt' ? `Bloqueado (${lockTimer}s)` : `Locked (${lockTimer}s)`}
                </>
              ) : isSubmitting ? (
                language === 'pt' ? 'Entrando...' : 'Logging in...'
              ) : (
                t.admin.login.submit
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
              <Lock size={12} />
              <span>{language === 'pt' ? 'Área de acesso restrito' : 'Restricted access area'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
